import { ResourceType } from '@cotera/api';
import {
  isTipTapTextNode,
  type ContentNode,
  type DocumentState,
  type TextNode,
} from './document-types';

export type WorkspaceResourceRef = {
  readonly resourceType: ResourceType;
  readonly resourceId: string;
};

function parseMentionData(attrs: Record<string, unknown> | undefined): {
  mentionType: string | undefined;
  resourceType: ResourceType | undefined;
  resourceId: string | undefined;
} {
  if (attrs === undefined) {
    return {
      mentionType: undefined,
      resourceType: undefined,
      resourceId: undefined,
    };
  }
  let mentionType: string | undefined;
  if (attrs['data'] !== undefined) {
    try {
      const data =
        typeof attrs['data'] === 'string'
          ? JSON.parse(attrs['data'] as string)
          : attrs['data'];
      mentionType = data?.t ?? data?.type;
      const inner = data?.data;
      if (
        typeof inner === 'object' &&
        inner !== null &&
        'resourceType' in inner &&
        'resourceId' in inner
      ) {
        const rt = String(inner['resourceType']);
        const id = String(inner['resourceId']);
        if (isResourceType(rt)) {
          return {
            mentionType,
            resourceType: rt,
            resourceId: id,
          };
        }
      }
    } catch {
      if (typeof attrs['data'] === 'string') {
        mentionType = attrs['data'];
      }
    }
  }
  return { mentionType, resourceType: undefined, resourceId: undefined };
}

function isResourceType(value: string): value is ResourceType {
  return (Object.values(ResourceType) as string[]).includes(value);
}

function walkContent(
  nodes: readonly (ContentNode | TextNode)[] | undefined,
  out: Map<string, WorkspaceResourceRef>
): void {
  if (!Array.isArray(nodes)) {
    return;
  }
  for (const node of nodes) {
    if (typeof node !== 'object' || node === null) {
      continue;
    }
    if (Array.isArray(node)) {
      walkContent(node, out);
      continue;
    }
    if (isTipTapTextNode(node)) {
      continue;
    }
    const cn = node as ContentNode;
    if (cn.type === 'mention') {
      const attrs = cn.attrs as Record<string, unknown> | undefined;
      const parsed = parseMentionData(attrs);
      if (
        parsed.resourceType !== undefined &&
        parsed.resourceId !== undefined
      ) {
        const key = `${parsed.resourceType}:${parsed.resourceId}`;
        out.set(key, {
          resourceType: parsed.resourceType,
          resourceId: parsed.resourceId,
        });
      } else if (
        (parsed.mentionType === 'agent' || parsed.mentionType === 'document') &&
        attrs !== undefined &&
        typeof attrs['id'] === 'string'
      ) {
        const id = attrs['id'];
        const resourceType =
          parsed.mentionType === 'agent'
            ? ResourceType.Agent
            : ResourceType.Document;
        const key = `${resourceType}:${id}`;
        out.set(key, {
          resourceType,
          resourceId: id,
        });
      }
    }
    walkContent(cn.content, out);
  }
}

/**
 * Collects unique workspace resource mentions (`mention` nodes with `data.t === 'resource'`)
 * from a TipTap document tree.
 */
export function collectWorkspaceResourceRefs(
  state: DocumentState
): readonly WorkspaceResourceRef[] {
  const map = new Map<string, WorkspaceResourceRef>();
  walkContent(state.content, map);
  return [...map.values()];
}
